/**
 * ActionCable WebSocket Service
 * Handles real-time communication with Rails backend via ActionCable
 */

import { createConsumer, Cable, Subscription } from '@rails/actioncable'
import { getStoredToken } from '../utils/token'

// WebSocket URL - same domain as API with /cable path
// @ts-ignore - Vite provides import.meta.env
const WS_BASE_URL = (import.meta as any).env?.VITE_WS_URL || 'wss://api.enzymic.co/cable'

let cable: Cable | null = null
let cableToken: string | null = null
const subscriptions: Map<number, Subscription> = new Map()

/**
 * Get or create ActionCable consumer
 * Includes JWT token for authentication.
 * Automatically reconnects if the stored token has changed (e.g. after re-login).
 */
export function getCable(): Cable | null {
  const token = getStoredToken()
  
  if (!token) {
    console.warn('ActionCable: No JWT token available')
    return null
  }

  if (cable && cableToken !== token) {
    console.log('ActionCable: Token changed, reconnecting')
    disconnectCable()
  }

  if (!cable) {
    const wsUrl = `${WS_BASE_URL}?token=${token}`
    cable = createConsumer(wsUrl)
    cableToken = token
  }

  return cable
}

/**
 * Disconnect and cleanup ActionCable consumer
 */
export function disconnectCable(): void {
  if (cable) {
    cable.disconnect()
    cable = null
    cableToken = null
  }
  subscriptions.clear()
}

/**
 * Reconnect ActionCable (useful after token refresh)
 */
export function reconnectCable(): void {
  disconnectCable()
  getCable()
}

/**
 * Callback interfaces for conversation subscription
 */
export interface AiCreativeCreationCompletedData {
  type: 'ai_creative_creation_completed'
  data: {
    status?: string
    custom_template_id?: number
    destination_url?: string
    ai_creative_resource?: {
      content_data?: { text?: Record<string, unknown>; image_prompt?: Record<string, unknown> }
      html_code?: string
    }
    ai_creative?: { image_data?: Record<string, unknown>; audio_data?: Record<string, unknown>; video_data?: Record<string, unknown> }
    ad_unit: { id: number; campaign_id: number; content_type: string; name: string; sizes: string[] }
  }
}

export interface ConversationCallbacks {
  onUpdate: (data: ConversationUpdateData) => void
  onAdUnitProcessing?: (data: PreviewCreativeStatusData) => void
  onAdUnitCreated?: (data: PreviewCreativeCreatedData) => void
  onMediaBuyComplete?: (data: MediaBuyCompleteData) => void
  onCreativeSyncCompleted?: (data: CreativeSyncCompletedData) => void
  onAiCreativeCreationCompleted?: (data: AiCreativeCreationCompletedData) => void
  onConnected?: () => void
  onDisconnected?: () => void
  onRejected?: () => void
}

/**
 * Base interface for all WebSocket messages
 */
export interface WebSocketMessage {
  type: string
  [key: string]: any
}

/**
 * Data structure received from WebSocket conversation updates
 */
export interface ConversationUpdateData extends WebSocketMessage {
  type: 'conversation_update'
  data: {
    id: number
    advertiser_id: number
    status: ConversationStatus
    query: string
    chat_history: ChatHistoryItem[]
    campaign?: AiCampaignData
    response_state: ResponseStateData
    created_at: string
    updated_at: string
  }
}

/**
 * Subscription confirmation message
 */
export interface SubscriptionConfirmation extends WebSocketMessage {
  type: 'subscribed'
  conversation_id: number
}

/**
 * Ad unit data from preview_creative_status message (processing state)
 */
export interface ProcessingAdUnit {
  id: number
  campaign_id: number
  content_type: string
  sizes: string[]
}

/**
 * Data structure received when ad unit creation starts (processing)
 * Type: preview_creative_status
 */
export interface PreviewCreativeStatusData extends WebSocketMessage {
  type: 'preview_creative_status'
  data: {
    status: 'processing' | 'failed'
    ad_unit: ProcessingAdUnit
  }
}

/**
 * Ad unit data from preview_creative_created message (completed with preview)
 */
export interface CreatedAdUnit {
  ad_unit_id: number
  campaign_id: number
  content_type: string
  sizes: string[]
  ad_ids?: number[]
  preview_url?: string
  preview_static_urls?: Array<{
    size: string
    url: string
  }>
}

/**
 * Data structure received when ad unit is fully created with preview URL
 * Type: preview_creative_created
 */
export interface PreviewCreativeCreatedData extends WebSocketMessage {
  type: 'preview_creative_created'
  data: {
    conversation_id: number
    ad_unit: CreatedAdUnit
  }
}

/**
 * Package data from media buy response
 */
export interface MediaBuyPackage {
  package_id: string
  product_id: string
  pricing_option_id: string
  budget: number
  buyer_ref: string
  paused: boolean
  /** Newer agentic_buy.create_media_buy adds a per-package canceled flag. */
  canceled?: boolean
  /**
   * Legacy media_buy_complete cable event always shipped impressions; the
   * newer agentic_buy.create_media_buy snapshot may omit it.
   */
  impressions?: number
  /** Optional in the agentic_buy.create_media_buy shape. */
  targeting_overlay?: {
    publisher_properties: Array<{
      property_tags: string[]
      publisher_domain: string
      selection_type: string
    }>
    geo_country_any_of: string[] | null
  }
  /**
   * agentic_buy.create_media_buy returns the creative formats the buyer must
   * provide for this package. Each item has the same { id, agent_url } shape
   * as SalesFormatId so the Sync-creatives view can iterate it directly.
   */
  format_ids_to_provide?: Array<{
    id: string
    agent_url?: string
  }>
}

/**
 * Media buy response data. Top-level fields are intentionally lenient
 * because agentic_buy.create_media_buy fills some of them with null.
 */
export interface MediaBuyData {
  media_buy_id: string
  status: string | null
  buyer_ref: string
  creative_deadline: string | null
  packages: MediaBuyPackage[]
}

/**
 * Data structure received when media buy is complete
 * Type: media_buy_complete
 */
export interface MediaBuyCompleteData extends WebSocketMessage {
  type: 'media_buy_complete'
  data: {
    success: boolean
    data: MediaBuyData
    metadata: {
      taskId: string
      status: string
    }
  }
}

/**
 * Creative sync result item
 */
export interface CreativeSyncResult {
  creative_id: string
  action: 'created' | 'updated' | string
}

/**
 * Data structure received when creative sync is complete
 * Type: creative_sync_completed
 */
export interface CreativeSyncCompletedData extends WebSocketMessage {
  type: 'creative_sync_completed'
  payload: {
    success: boolean
    data: {
      creatives: CreativeSyncResult[]
    }
    metadata: {
      taskId: string
      status: string
    }
    enzymic_ad_unit_id: number
    enzymic_campaign_id: number
  }
}

export type ConversationStatus = 
  | 'pending' 
  | 'in_progress' 
  | 'completed' 
  | 'awaiting_input' 
  | 'failed'

export interface ChatHistoryItem {
  role:
    | 'user'
    | 'OrchestratorAgent'
    | 'CampaignAgent'
    | 'TemplateConceptAgent'
    | 'SocialMediaRecommendationAgent'
    | string
  message: string
  /**
   * Optional semantic marker on agent turns. Used by pickers to render
   * an inline card grid where the agent's message appears in chat.
   *   • `'concepts'`         — TemplateConceptAgent
   *   • `'social_templates'` — SocialMediaRecommendationAgent
   * See `src/types/index.ts` for the full spec.
   */
  type?: 'concepts' | 'social_templates' | string
  /**
   * Optional structured payload attached to agent turns. For
   * `type: 'concepts'` it carries the ids of `response_state
   * .template_concepts.items`; for `type: 'social_templates'` it
   * carries the ids of `response_state.social_media_recommended_template
   * .items`. Both are resolved at render time so historical turns keep
   * showing fresh status after later picker updates.
   */
  ref?: {
    concept_ids?: string[]
    social_template_ids?: string[]
    [key: string]: unknown
  }
}

/**
 * Check if a chat history item is from the user
 */
export function isUserMessage(item: ChatHistoryItem): boolean {
  return item.role.toLowerCase() === 'user'
}

/**
 * Check if a chat history item is from the OrchestratorAgent
 */
export function isOrchestratorMessage(item: ChatHistoryItem): boolean {
  return item.role === 'OrchestratorAgent'
}

/**
 * Check if a chat history item is from the CampaignAgent
 */
export function isCampaignAgentMessage(item: ChatHistoryItem): boolean {
  return item.role === 'CampaignAgent'
}

/**
 * Check if a chat history item is from the TemplateConceptAgent — the
 * agent that drives the `ai_creative_concept` workflow's picker. Its
 * turns render as a text bubble plus an inline concept-card grid
 * (see `type: 'concepts'` + `ref.concept_ids`).
 */
export function isTemplateConceptMessage(item: ChatHistoryItem): boolean {
  return item.role === 'TemplateConceptAgent'
}

/**
 * Check if a chat history item is from the SocialMediaRecommendationAgent —
 * the agent that drives the social-templates picker. Its turns render as
 * a text bubble plus an inline social-template card grid (see
 * `type: 'social_templates'` + `ref.social_template_ids`).
 */
export function isSocialTemplateMessage(item: ChatHistoryItem): boolean {
  return item.role === 'SocialMediaRecommendationAgent'
}

/**
 * Check if a chat history item is from any AI agent
 */
export function isAIMessage(item: ChatHistoryItem): boolean {
  return (
    isOrchestratorMessage(item) ||
    isCampaignAgentMessage(item) ||
    isTemplateConceptMessage(item) ||
    isSocialTemplateMessage(item)
  )
}

/**
 * Check if a chat history item should be displayed in the chat
 */
export function isDisplayableMessage(item: ChatHistoryItem): boolean {
  return (
    isUserMessage(item) ||
    isOrchestratorMessage(item) ||
    isCampaignAgentMessage(item) ||
    isTemplateConceptMessage(item) ||
    isSocialTemplateMessage(item)
  )
}

/**
 * Format CampaignAgent message content as structured campaign details
 * Parses the message to extract and format overview, objectives, period
 */
export function formatCampaignAgentMessage(message: string): string {
  // If the message already contains formatted sections, return as-is
  if (message.includes('**Overview**') || message.includes('**Objectives**')) {
    return message
  }
  
  // Try to parse if it's JSON-like content
  try {
    const data = JSON.parse(message)
    return formatCampaignDetailsForChat(data)
  } catch {
    // Not JSON, return the message as-is with some formatting
    return message
  }
}

export interface AiCampaignData {
  id: number
  overview: string
  objectives: string[]
  period: string
  campaign_id: number // Links to 1.0 Campaign
  media_plans?: any[]
}

/**
 * Check if campaign has displayable data (overview, objectives, or period)
 */
export function hasCampaignDetails(campaign: AiCampaignData | undefined | null): boolean {
  if (!campaign) return false
  return !!(
    campaign.overview?.trim() || 
    (campaign.objectives && campaign.objectives.length > 0) || 
    campaign.period?.trim()
  )
}

/**
 * Format campaign details (overview, objectives, period) for chat display
 */
export function formatCampaignDetailsForChat(campaign: AiCampaignData | undefined | null): string {
  if (!campaign) return ''
  
  const sections: string[] = []
  
  if (campaign.overview?.trim()) {
    sections.push(`**Overview**\n${campaign.overview.trim()}`)
  }
  
  if (campaign.objectives && campaign.objectives.length > 0) {
    const list = campaign.objectives.map((obj: string) => `• ${obj}`).join('\n')
    sections.push(`**Objectives**\n${list}`)
  }
  
  if (campaign.period?.trim()) {
    sections.push(`**Campaign Period**\n${campaign.period.trim()}`)
  }
  
  return sections.join('\n\n')
}

export interface ResponseStateData {
  overview?: string
  objectives?: string[]
  period?: string
  pricing?: any[]
  chat_history?: ChatHistoryItem[]
  media_plan?: any
  template_recommendation?: any
  template_recommendations?: any
  sitelists?: any
  /**
   * Newer agentic-buy container (replaces legacy sales_recommendation(s)).
   * Shape: { status, current_node, get_product: { products, count }, sync_creative?, create_media_buy?, ... }
   */
  agentic_buy?: any
  /**
   * AI creative generation container. Once the workflow completes, this
   * holds the persisted `ad_unit_id` (and any associated resource data) so
   * the frontend can navigate the user into the Creative Studio without
   * waiting for a separate cable event.
   */
  /**
   * Reference assets the user uploaded with their request. Stored on the
   * conversation as a stable name → URL map, so we can render thumbnails
   * in the chat after refresh / reconnect.
   */
  input_reference_data?: {
    input_image_data?: Record<string, string>
    input_audio_data?: Record<string, string>
    input_video_data?: Record<string, string>
    [key: string]: unknown
  }
  ai_creative_generation?: {
    ad_unit_id?: number
    ai_creative_data?: Record<string, unknown>
    ai_creative_resource?: Record<string, unknown>
    [key: string]: unknown
  }
  /**
   * Social-media workflow's counterpart to
   * `ai_creative_generation.ad_unit_id` — the BE writes the persisted
   * ad unit id here as a scalar once the social template creation
   * finishes. Reaches the "Go to Creative" button through the
   * standard `hasAnyResults` / `pickResultTarget` gate.
   */
  social_creative_ad_unit_id?: number
  /**
   * Live picker state for the `ai_creative_concept` workflow. Present
   * whenever the workflow is active; the FE resolves concept ids
   * referenced by `TemplateConceptAgent` chat turns against
   * `template_concepts.items` at render time so per-item status
   * (`refined`, `finalized`) stays fresh on historical turns.
   */
  template_concepts?: {
    phase?: 'awaiting_selection' | 'refining' | 'finalized' | 'skipped' | string
    items?: Array<{
      id: string
      title?: string
      summary?: string
      thumbnail_url?: string
      status?: 'proposed' | 'refined' | 'finalized' | string
      parent_id?: string | null
      [key: string]: unknown
    }>
    selected_concept_id?: string | null
    [key: string]: unknown
  }
  /**
   * Live picker state for the `SocialMediaRecommendationAgent` workflow.
   * Structural sibling of `template_concepts`; the FE resolves social
   * template ids referenced by chat turns (`type: 'social_templates'`,
   * `ref.social_template_ids`) against `items` at render time.
   */
  social_media_recommended_template?: {
    phase?: 'awaiting_selection' | 'refining' | 'finalized' | 'skipped' | string
    items?: Array<{
      id: string
      title?: string
      description?: string
      summary?: string
      format_id?: string
      preview_url?: string
      screenshot?: string
      status?: 'proposed' | 'refined' | 'finalized' | string
      parent_id?: string | null
      [key: string]: unknown
    }>
    selected_id?: string | null
    [key: string]: unknown
  }
  next_node?: string
  [key: string]: any
}

/**
 * Check if response_state has media_plan data
 */
export function hasMediaPlan(responseState: ResponseStateData | undefined | null): boolean {
  if (!responseState?.media_plan) return false
  if (typeof responseState.media_plan === 'object') {
    return Object.keys(responseState.media_plan).length > 0
  }
  return false
}

const RESULT_KEYS = [
  'template_recommendation', 'template_recommendations',
  'sitelists',
  'agentic_buy',
  'media_plan',
  // Light up the "Go to ..." button on the Home chat as soon as
  // ai_creative_generation persists an ad_unit_id.
  'ai_creative_generation',
  // Social-media workflow analog — the BE writes the finished ad
  // unit's id directly onto response_state as a scalar, rather than
  // nesting it under an object like `ai_creative_generation`.
  'social_creative_ad_unit_id',
] as const

/**
 * Decide whether a single result-key container actually carries data
 * (vs. just being a scaffolded shell the agent prefills during intake).
 *
 * The trap we're solving: while the `requirements` workflow is still
 * gathering input, the backend may already set keys like
 *   { template_recommendation: { recommendations: [] } }
 *   { agentic_buy: { get_product: { products: [] } } }
 *   { media_plan: { items: [] } }
 * which would pass a naive "object has keys" check and light up the
 * "Go to Campaign" button prematurely. We instead require the *terminal*
 * payload arrays/objects to be non-empty before counting a key as
 * meaningful. Unknown future keys fall back to the loose check so we
 * don't accidentally hide a brand-new result type.
 */
function resultKeyHasData(key: string, val: unknown): boolean {
  if (val == null) return false
  // Scalar result keys — the BE writes some completions as a bare
  // primitive on response_state (e.g. `social_creative_ad_unit_id: 42`)
  // rather than nesting them inside a container object. Handle those
  // before the array/object guards below, otherwise the number would
  // be dismissed as "not an object" and the button gate would miss.
  if (key === 'social_creative_ad_unit_id') {
    return typeof val === 'number' && val > 0
  }
  if (Array.isArray(val)) return val.length > 0
  if (typeof val !== 'object') return false
  const obj = val as Record<string, any>

  switch (key) {
    case 'template_recommendation':
    case 'template_recommendations':
      return (
        (Array.isArray(obj.recommendations) && obj.recommendations.length > 0) ||
        (Array.isArray(obj.templates) && obj.templates.length > 0) ||
        (Array.isArray(obj.high_impact_templates) && obj.high_impact_templates.length > 0) ||
        (Array.isArray(obj.programmatic_templates) && obj.programmatic_templates.length > 0)
      )
    case 'sitelists':
      return (
        (Array.isArray(obj.sitelists) && obj.sitelists.length > 0) ||
        (Array.isArray(obj.items) && obj.items.length > 0) ||
        (Array.isArray(obj.sites) && obj.sites.length > 0)
      )
    case 'agentic_buy':
      // Real data lives one level deeper under `get_product.products` /
      // `create_media_buy.*` / `sync_creative.*`. `status` / `current_node`
      // are workflow scaffolding and shouldn't trip the gate.
      return (
        (Array.isArray(obj?.get_product?.products) && obj.get_product.products.length > 0) ||
        (typeof obj?.get_product?.count === 'number' && obj.get_product.count > 0) ||
        (obj.create_media_buy && typeof obj.create_media_buy === 'object' &&
          Object.keys(obj.create_media_buy).length > 0) ||
        (obj.sync_creative && typeof obj.sync_creative === 'object' &&
          Object.keys(obj.sync_creative).length > 0)
      )
    case 'media_plan':
      // The media plan container starts out empty `{}` and accrues
      // packages / items / channels as the planner agent fills it in.
      return (
        (Array.isArray(obj.packages) && obj.packages.length > 0) ||
        (Array.isArray(obj.items) && obj.items.length > 0) ||
        (Array.isArray(obj.channels) && obj.channels.length > 0) ||
        (Array.isArray(obj.placements) && obj.placements.length > 0) ||
        (Array.isArray(obj.media_plan) && obj.media_plan.length > 0) ||
        typeof obj.campaign_name === 'string' && obj.campaign_name.trim().length > 0
      )
    case 'ai_creative_generation':
      // We only want to navigate the user to the Creative Studio once
      // the agent has persisted a concrete ad unit they can open.
      return typeof obj.ad_unit_id === 'number' && obj.ad_unit_id > 0
    default:
      // Unknown key — keep the loose "non-empty object" check so future
      // result types don't silently disappear from the gate.
      return Object.keys(obj).length > 0
  }
}

/**
 * Check if response_state has ANY result data (not just media_plan).
 * Used to show the "Go to Campaign" button as soon as any agent produces
 * actionable output. Callers that also need to verify the BE created a
 * Campaign row should AND this with the conversation's campaign_id —
 * `response_state` alone doesn't guarantee a navigable campaign.
 */
export function hasAnyResults(responseState: ResponseStateData | undefined | null): boolean {
  if (!responseState) return false
  return RESULT_KEYS.some(key => resultKeyHasData(key, responseState[key]))
}

/**
 * Map a response_state snapshot to the most relevant Campaign-page tab to
 * deep-link into when the user clicks "Go to Campaign" from Home. The hash
 * value matches the routes recognised by the Campaigns page (see
 * TAB_HASH_MAP / tabIdMap in src/pages/Campaigns.tsx). When no result keys
 * are populated yet, returns the proposal/Campaign default.
 *
 * Priority order is intentional:
 *   1. agentic_buy.get_product.products  -> Media Buy (most actionable)
 *   2. media_plan / sitelists / templates -> Campaign Proposal
 *   3. fallback                           -> Campaign Proposal
 */
export interface ResultTarget {
  /** Stable tab id used by Campaigns.tsx (proposal | mediaBuy | creative | ...) */
  tabId: string
  /** URL hash fragment (e.g. 'mediabuy', 'campaignproposal') */
  hash: string
  /** Human-friendly label for the "Go to ..." button */
  label: string
  /**
   * When set, the caller should deep-link directly into a specific ad unit
   * (Creative Studio) instead of just hash-anchoring on the campaign page.
   */
  adUnitId?: number
}

export function pickResultTarget(
  responseState: ResponseStateData | undefined | null,
): ResultTarget {
  const proposal: ResultTarget = {
    tabId: 'proposal',
    hash: 'campaignproposal',
    label: 'Campaign',
  }
  if (!responseState) return proposal

  // 1. Creative generation wins when it has produced an ad_unit_id — we
  //    take the user straight to the Creative Studio for that unit.
  //    Two shapes are accepted, in priority order:
  //      • `ai_creative_generation.ad_unit_id`     (main flow)
  //      • `social_creative_ad_unit_id` (scalar)   (social-media flow)
  //    They mean the same thing to the UI — a persisted ad unit id
  //    the user should be able to open in the Creative Studio.
  const aiGen = (responseState as any).ai_creative_generation
  if (resultKeyHasData('ai_creative_generation', aiGen)) {
    return {
      tabId: 'creative',
      hash: 'creativegeneration',
      label: 'Creative',
      adUnitId: typeof aiGen?.ad_unit_id === 'number' ? aiGen.ad_unit_id : undefined,
    }
  }
  const socialAdUnitId = (responseState as any).social_creative_ad_unit_id
  if (resultKeyHasData('social_creative_ad_unit_id', socialAdUnitId)) {
    return {
      tabId: 'creative',
      hash: 'creativegeneration',
      label: 'Creative',
      adUnitId: socialAdUnitId as number,
    }
  }

  // 2. Agentic Buy products → Media Buy tab.
  if (resultKeyHasData('agentic_buy', (responseState as any).agentic_buy)) {
    return { tabId: 'mediaBuy', hash: 'mediabuy', label: 'Media Buy' }
  }

  // 3. Anything proposal-shaped (media plan / sitelists / template recs) →
  //    Proposal tab. We reuse the same data-shape gate as hasAnyResults
  //    so an empty `{}` shell during intake doesn't masquerade as a hit.
  if (hasProposalData(responseState)) {
    return proposal
  }

  return proposal
}

/**
 * Whether a `response_state` snapshot has any Campaign-Proposal-shaped
 * data on it. Kept in sync with the `getVisibleTabs.proposal` gate on
 * the Campaigns page so callers (like the "View Campaign" button on
 * Home) can pick the right default tab without duplicating the check.
 */
export function hasProposalData(
  responseState: ResponseStateData | undefined | null,
): boolean {
  if (!responseState) return false
  return (
    resultKeyHasData('media_plan', responseState.media_plan) ||
    resultKeyHasData('sitelists', responseState.sitelists) ||
    resultKeyHasData('template_recommendation', responseState.template_recommendation) ||
    resultKeyHasData(
      'template_recommendations',
      (responseState as any).template_recommendations,
    )
  )
}

/**
 * Check if the orchestrator workflow has ended (next_node === 'end')
 */
export function isWorkflowComplete(responseState: ResponseStateData | undefined | null): boolean {
  if (!responseState) return false
  return responseState.next_node === 'end'
}

/**
 * Check if thinking bubble should be shown
 * Shows thinking after any message until orchestrator next_node === 'end'
 */
export function shouldShowThinking(
  responseState: ResponseStateData | undefined | null,
  chatHistory: ChatHistoryItem[] | undefined,
  status: string
): boolean {
  // If status is failed, don't show thinking
  if (status === 'failed') return false
  
  // If workflow is complete (next_node === 'end'), don't show thinking
  if (isWorkflowComplete(responseState)) return false
  
  // Show thinking if we have any chat messages and next_node is not 'end'
  // This ensures thinking shows after every orchestrator response until workflow ends
  if (chatHistory && chatHistory.length > 0) {
    return true
  }
  
  // Show thinking if status is pending or in_progress (initial state before any messages)
  if (status === 'pending' || status === 'in_progress') return true
  
  return false
}

/**
 * Subscribe to a specific conversation channel
 * Receives real-time updates when AI processes the conversation
 */
export function subscribeToConversation(
  conversationId: number,
  callbacks: ConversationCallbacks
): Subscription | null {
  const consumer = getCable()
  
  if (!consumer) {
    console.error('ActionCable: Cannot subscribe - no cable connection')
    return null
  }

  // Unsubscribe from existing subscription if any
  unsubscribeFromConversation(conversationId)

  const subscription = consumer.subscriptions.create(
    { channel: 'AiConversationChannel', id: conversationId },
    {
      connected() {
        console.log(`ActionCable: Connected to conversation ${conversationId}`)
        callbacks.onConnected?.()
      },
      disconnected() {
        console.log(`ActionCable: Disconnected from conversation ${conversationId}`)
        callbacks.onDisconnected?.()
      },
      rejected() {
        console.error(`ActionCable: Subscription rejected for conversation ${conversationId}`)
        callbacks.onRejected?.()
      },
      received(data: WebSocketMessage) {
        console.log(`ActionCable: Received message for conversation ${conversationId}`, data)
        
        // Process different message types
        switch (data.type) {
          case 'conversation_update':
            callbacks.onUpdate(data as ConversationUpdateData)
            break
          case 'preview_creative_status':
            // Ad unit creation started (processing state)
            if (callbacks.onAdUnitProcessing) {
              callbacks.onAdUnitProcessing(data as PreviewCreativeStatusData)
            } else {
              console.log(`ActionCable: No handler for preview_creative_status`)
            }
            break
          case 'preview_creative_created':
            // Ad unit fully created with preview URL
            if (callbacks.onAdUnitCreated) {
              callbacks.onAdUnitCreated(data as PreviewCreativeCreatedData)
            } else {
              console.log(`ActionCable: No handler for preview_creative_created`)
            }
            break
          case 'media_buy_complete':
            // Media buy creation completed
            if (callbacks.onMediaBuyComplete) {
              callbacks.onMediaBuyComplete(data as MediaBuyCompleteData)
            } else {
              console.log(`ActionCable: No handler for media_buy_complete`)
            }
            break
          case 'creative_sync_completed':
            if (callbacks.onCreativeSyncCompleted) {
              callbacks.onCreativeSyncCompleted(data as CreativeSyncCompletedData)
            } else {
              console.log(`ActionCable: No handler for creative_sync_completed`)
            }
            break
          case 'ai_creative_creation_completed':
            if (callbacks.onAiCreativeCreationCompleted) {
              callbacks.onAiCreativeCreationCompleted(data as AiCreativeCreationCompletedData)
            } else {
              console.log(`ActionCable: No handler for ai_creative_creation_completed`)
            }
            break
          default:
            console.log(`ActionCable: Ignoring message type "${data.type}"`)
        }
      }
    }
  )

  subscriptions.set(conversationId, subscription)
  return subscription
}

/* =================================================================
 *  BrandKitChannel – real-time extraction status
 * ================================================================= */

export interface BrandKitExtractionCallbacks {
  onExtractionStatus?: (data: BrandKitExtractionStatusData) => void
  onExtractionCompleted?: (data: BrandKitExtractionCompletedData) => void
  onExtractionFailed?: (data: BrandKitExtractionFailedData) => void
  onConnected?: () => void
  onDisconnected?: () => void
  onRejected?: () => void
}

export interface BrandKitExtractionStatusData {
  type: 'extraction_status'
  data: {
    status: 'extracting' | 'failed'
    brand_kit_id: number
    error?: string
  }
}

export interface BrandKitExtractionCompletedData {
  type: 'extraction_completed'
  data: {
    brand_kit_id: number
    status: 'completed'
    brand_data: Record<string, any>
  }
}

export interface BrandKitExtractionFailedData {
  type: 'extraction_failed'
  data: {
    brand_kit_id: number
    status: 'failed'
    error: string
  }
}

const brandKitSubscriptions: Map<number, Subscription> = new Map()

/**
 * Subscribe to a BrandKitChannel for extraction results.
 * After calling POST /brand-kits/extract, subscribe with the returned brand_kit.id.
 */
export function subscribeToBrandKit(
  brandKitId: number,
  callbacks: BrandKitExtractionCallbacks
): Subscription | null {
  const consumer = getCable()

  if (!consumer) {
    console.error('ActionCable: Cannot subscribe to BrandKitChannel – no cable connection')
    return null
  }

  unsubscribeFromBrandKit(brandKitId)

  const subscription = consumer.subscriptions.create(
    { channel: 'BrandKitChannel', id: brandKitId },
    {
      connected() {
        console.log(`ActionCable: Connected to BrandKitChannel ${brandKitId}`)
        callbacks.onConnected?.()
      },
      disconnected() {
        console.log(`ActionCable: Disconnected from BrandKitChannel ${brandKitId}`)
        callbacks.onDisconnected?.()
      },
      rejected() {
        console.error(`ActionCable: BrandKitChannel subscription rejected for ${brandKitId}`)
        callbacks.onRejected?.()
      },
      received(data: WebSocketMessage) {
        console.log(`ActionCable: BrandKitChannel ${brandKitId} received:`, data)

        switch (data.type) {
          case 'extraction_status':
            if ((data as any).data?.status === 'failed') {
              callbacks.onExtractionFailed?.({
                type: 'extraction_failed',
                data: { brand_kit_id: brandKitId, status: 'failed', error: (data as any).data?.error || 'Extraction failed' },
              })
            } else {
              callbacks.onExtractionStatus?.(data as BrandKitExtractionStatusData)
            }
            break
          case 'extraction_completed':
            callbacks.onExtractionCompleted?.(data as BrandKitExtractionCompletedData)
            break
          case 'extraction_failed':
            callbacks.onExtractionFailed?.(data as BrandKitExtractionFailedData)
            break
          default:
            console.log(`ActionCable: BrandKitChannel ignoring message type "${data.type}"`)
        }
      },
    }
  )

  brandKitSubscriptions.set(brandKitId, subscription)
  return subscription
}

export function unsubscribeFromBrandKit(brandKitId: number): void {
  const subscription = brandKitSubscriptions.get(brandKitId)
  if (subscription) {
    subscription.unsubscribe()
    brandKitSubscriptions.delete(brandKitId)
    console.log(`ActionCable: Unsubscribed from BrandKitChannel ${brandKitId}`)
  }
}

/**
 * Unsubscribe from a conversation channel
 */
export function unsubscribeFromConversation(conversationId: number): void {
  const subscription = subscriptions.get(conversationId)
  
  if (subscription) {
    subscription.unsubscribe()
    subscriptions.delete(conversationId)
    console.log(`ActionCable: Unsubscribed from conversation ${conversationId}`)
  }
}

/**
 * Check if subscribed to a conversation
 */
export function isSubscribedToConversation(conversationId: number): boolean {
  return subscriptions.has(conversationId)
}

/**
 * Get all active subscription IDs
 */
export function getActiveSubscriptions(): number[] {
  return Array.from(subscriptions.keys())
}

